import { useMemo } from "react"

import { useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { DirectionsCarIcon, LocalShippingIcon } from "@l2r-front/l2r-icons"
import { Typography } from "@l2r-front/l2r-ui"
import { formatNumber } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { useRiskZonesStateContext } from "../../contexts/RiskZonesContext"
import { useRiskZoneCluster } from "../../hooks/queries/useRiskZoneClusters"
import { useRiskZonesSurveys } from "../../hooks/queries/useRiskZonesSurveys"
import { useRiskZonesTraffic } from "../../hooks/queries/useRiskZonesTraffic"

import * as Styled from "./RiskZonesTrafficInfo.styled"

export function RiskZonesTrafficInfo() {

    const { riskZoneId, road } = useParams()
    const { t } = useTranslation(I18N_NAMESPACE)
    const { currentYear } = useRiskZonesStateContext()
    const {
        data: riskZone,
    } = useRiskZoneCluster(riskZoneId, {}, { enabled: !!riskZoneId })

    const queryParams = {
        year: currentYear,
        ...(road && { linear_location_road: road }),
        ...(riskZone && { linear_location_start: riskZone[0].linear_location.start }),
        ...(riskZone && { linear_location_end: riskZone[0].linear_location.end }),
    }
    const { data, isLoading, isError } = useRiskZonesTraffic(queryParams)

    const { data: surveys, isLoading: isSurveysLoading, isError: isSurveysError } = useRiskZonesSurveys()

    const hasSurvey = useMemo(() => {
        if (!surveys || !surveys.length) {
            return false
        }
        return surveys.some(survey => survey.year === currentYear)
    }, [surveys, currentYear])

    const trafficInfo = useMemo(() => {
        if (!data || !data.length) {
            return null
        }
        const hasData = data.some(item => item.sum_light_vehicles || item.sum_light_vehicles === 0 || item.sum_heavy_vehicles || item.sum_heavy_vehicles === 0)
        if (!hasData) {
            return null
        }
        const info = data.reduce((acc, item) => {
            acc.average_light_vehicle_count += item.sum_light_vehicles || 0
            acc.average_heavy_vehicle_count += item.sum_heavy_vehicles || 0
            return acc
        }, {
            average_light_vehicle_count: 0,
            average_heavy_vehicle_count: 0,
        })
        return {
            average_light_vehicle_count: Math.round(info.average_light_vehicle_count / data.length),
            average_heavy_vehicle_count: Math.round(info.average_heavy_vehicle_count / data.length),
        }
    }, [data])

    if (isLoading || isSurveysLoading) {
        return <Styled.Skeleton />
    }

    if (isError || isSurveysError) {
        return <Styled.Container>
            <Styled.ErrorText>
                {t(I18N_NAMESPACE, "containers.riskZonesTrafficInfo.error")}
            </Styled.ErrorText>
        </Styled.Container>
    }

    if (!hasSurvey || !data || !data.length) {
        return null
    }

    return <Styled.Container>
        <Styled.Title variant="Desktop & Mobile/Heading/H2">
            {t(I18N_NAMESPACE, `containers.riskZonesTrafficInfo.title.${riskZoneId ? "singular" : "plural"}`)}
        </Styled.Title>
        <Styled.ContentContainer>
            <Styled.TrafficStatContainer>
                <DirectionsCarIcon />
                <Typography>
                    {`${t(I18N_NAMESPACE, "containers.riskZonesTrafficInfo.lightCountLabel")} ${trafficInfo ? t(I18N_NAMESPACE, "containers.riskZonesTrafficInfo.countPerDay", {
                        count: formatNumber(trafficInfo.average_light_vehicle_count),
                    }) : t(I18N_NAMESPACE, "containers.riskZonesTrafficInfo.unknown")}`}
                </Typography>
            </Styled.TrafficStatContainer>
            <Styled.TrafficStatContainer>
                <LocalShippingIcon />
                <Typography>
                    {`${t(I18N_NAMESPACE, "containers.riskZonesTrafficInfo.heavyCountLabel")} ${trafficInfo ? t(I18N_NAMESPACE, "containers.riskZonesTrafficInfo.countPerDay", {
                        count: formatNumber(trafficInfo.average_heavy_vehicle_count),
                    }) : t(I18N_NAMESPACE, "containers.riskZonesTrafficInfo.unknown")}`}
                </Typography>
            </Styled.TrafficStatContainer>
        </Styled.ContentContainer>
    </Styled.Container>

}